import { FC } from 'react';
import { Table } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import { Test } from '@/types/evals-types';
import { DATE_FORMAT_PATTERN_WITH_TIME } from '@/constants';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface TestsConfigTableProps {
  testsConfigs: Test[];
  isLoading: boolean;
  setSelectedTestId: (testId: string) => void;
  setIsDeleteModalOpen: (isOpen: boolean) => void;
}

export const TestsConfigTable: FC<TestsConfigTableProps> = ({
  testsConfigs,
  isLoading,
  setSelectedTestId,
  setIsDeleteModalOpen,
}) => {
  const navigate = useOrgNavigate();

  const columns: ColumnDef<Test, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'test_type',
      header: 'Type',
      cell: () => {
        return <span>Content</span>;
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => {
        return (
          <span>
            {format(row.original.created_at, DATE_FORMAT_PATTERN_WITH_TIME)}
          </span>
        );
      },
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon'>
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  variant='destructive'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTestId(row.original.id);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <Trash />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const handleRowClick = (testId: string) => {
    navigate(generatePath(ROUTES.EVALUATION.TESTS_SETTINGS, { testId }));
  };

  return (
    <Table
      columns={columns}
      data={testsConfigs}
      loading={isLoading}
      onRowClick={(row) => row.id && handleRowClick(row.id)}
    />
  );
};
